import { NetworkStatus, useQuery } from "@apollo/client";
import {
  Button,
  CurrencyAmountUI,
  DataTable,
  Number,
  PrimaryFullScreenSpinner,
} from "@repo/ui";
import { isCurrency, NotFoundError } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Suspense } from "react";
import { EntityLink } from "~/components/link/EntityLink/EntityLink";
import { graphql } from "~/gql/client";
import type { AdminLedgerAccountEntriesQuery } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute(
  "/__auth/ledger/accounts_/$accountId/entries",
)({
  head: () => opsHead("Ledger Account Entries"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const entriesDocument = graphql(`
  query AdminLedgerAccountEntries($after: String, $first: Int!, $filter: LedgerEntriesPageFilter!) {
    ledgerEntries(after: $after, first: $first, filter: $filter) {
      pageInfo {
        endCursor
        hasNextPage
      }
      ledgerEntries {
        id
        direction
        unitAmount {
          amount
          unit
        }
        transaction {
          id
          effectiveAt
          status
        }
      }
    }
  }
`);

type LedgerEntryRow = NonNullable<
  AdminLedgerAccountEntriesQuery["ledgerEntries"]
>["ledgerEntries"][number];

const columns: ColumnDef<LedgerEntryRow>[] = [
  {
    id: "effectiveAt",
    accessorFn: (row) => row.transaction?.effectiveAt,
    header: "Effective Date",
    cell: ({ row }) => {
      const effectiveAt = row.original.transaction?.effectiveAt;
      return effectiveAt ? effectiveAt.toLocaleDateString() : "-";
    },
    enableSorting: false,
  },
  {
    id: "direction",
    accessorFn: (row) => row.direction,
    header: "Direction",
    enableSorting: false,
  },
  {
    id: "amount",
    accessorFn: (row) => row.unitAmount.amount,
    header: "Amount",
    cell: ({ row }) => {
      const unit = row.original.unitAmount.unit;
      const amount = row.original.unitAmount.amount;
      return isCurrency(unit) ? (
        <CurrencyAmountUI amount={amount} currency={unit} />
      ) : (
        <Number value={amount} format={Number.format.units(amount)} />
      );
    },
    enableSorting: false,
  },
  {
    id: "status",
    accessorFn: (row) => row.transaction?.status,
    header: "Status",
    cell: ({ row }) => row.original.transaction?.status ?? "-",
    enableSorting: false,
  },
  {
    id: "transactionId",
    accessorFn: (row) => row.transaction?.id,
    header: "Transaction ID",
    cell: ({ row }) => {
      const transactionId = row.original.transaction?.id;
      return transactionId ? (
        <EntityLink to={`/ledger/transactions/${transactionId}`}>
          {transactionId}
        </EntityLink>
      ) : (
        "-"
      );
    },
    enableSorting: false,
  },
];

function View() {
  const { accountId } = Route.useParams();
  if (!accountId) {
    throw new NotFoundError();
  }

  const baseInput = {
    first: 20,
    filter: { ledgerAccountIds: [accountId] },
  };

  const { data, fetchMore, loading, networkStatus } = useQuery(
    entriesDocument,
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const ledgerEntries = data?.ledgerEntries.ledgerEntries ?? [];
  const nextCursor = data?.ledgerEntries.pageInfo.hasNextPage
    ? (data.ledgerEntries.pageInfo.endCursor ?? undefined)
    : undefined;

  return (
    <div>
      <DataTable
        data={ledgerEntries}
        isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
        hidePagination
        columns={columns}
        manualSorting
        manualPagination
        pageSize={10_000}
      />
      <div className="mt-4 flex w-full flex-row justify-center">
        {nextCursor ? (
          <Button
            onClick={() =>
              fetchMore({ variables: { ...baseInput, after: nextCursor } })
            }
            variant="outline"
            loading={loading || networkStatus === NetworkStatus.fetchMore}
          >
            Load more
          </Button>
        ) : (
          !!ledgerEntries.length && (
            <p className="text-secondary text-sm">No more data</p>
          )
        )}
      </div>
    </div>
  );
}
